import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/api-client";

const CONVERSATIONS_QUERY_KEYS = {
    all: ["conversations"] as const,
    lists: () => [...CONVERSATIONS_QUERY_KEYS.all, "list"] as const,
    list: (filters?: {
        agent_id?: string;
        is_active?: boolean;
        starred?: boolean;
        skip?: number;
        limit?: number;
        q?: string;
    }) => [...CONVERSATIONS_QUERY_KEYS.lists(), filters] as const,
    latest: (includeMessages?: boolean) =>
        [...CONVERSATIONS_QUERY_KEYS.all, "latest", includeMessages] as const,
    detail: (id: string) => [...CONVERSATIONS_QUERY_KEYS.all, "detail", id] as const,
};

export interface ConversationMessageResponse {
    message_id: string;
    role: "user" | "assistant";
    content: string;
    created_at: string;
}

export interface ConversationResponse {
    conversation_id: string;
    title: string | null;
    message_count: number;
    started_at: string;
    last_message_at: string | null;
    starred: boolean;
    messages?: ConversationMessageResponse[];
}

export interface ConversationUpdate {
    title?: string | null;
    starred?: boolean;
}

export interface BulkDeleteRequest {
    conversation_ids: string[];
}

export function useConversations(filters?: {
    agent_id?: string;
    is_active?: boolean;
    starred?: boolean;
    skip?: number;
    limit?: number;
    q?: string;
}) {
    return useQuery({
        queryKey: CONVERSATIONS_QUERY_KEYS.list(filters),
        queryFn: async (): Promise<ConversationResponse[]> => {
            const params: Record<string, string> = {};
            if (filters?.agent_id) params.agent_id = filters.agent_id;
            if (filters?.is_active !== undefined) params.is_active = String(filters.is_active);
            if (filters?.starred !== undefined) params.starred = String(filters.starred);
            if (filters?.skip !== undefined) params.skip = String(filters.skip);
            if (filters?.limit !== undefined) params.limit = String(filters.limit);
            const q = filters?.q?.trim();
            if (q) params.q = q;
            return apiGet<ConversationResponse[]>("/conversations", { params });
        },
    });
}

export function useConversationsInfinite(filters?: {
    agent_id?: string;
    is_active?: boolean;
    starred?: boolean;
    limit?: number;
    q?: string;
}) {
    const limit = filters?.limit ?? 20;
    const qTrimmed = filters?.q?.trim() || undefined;

    return useInfiniteQuery({
        queryKey: [...CONVERSATIONS_QUERY_KEYS.lists(), "infinite", { ...filters, q: qTrimmed }],
        queryFn: async ({ pageParam = 0 }): Promise<ConversationResponse[]> => {
            const params: Record<string, string> = {
                skip: String(pageParam),
                limit: String(limit),
            };
            if (filters?.agent_id) params.agent_id = filters.agent_id;
            if (filters?.is_active !== undefined) params.is_active = String(filters.is_active);
            if (filters?.starred !== undefined) params.starred = String(filters.starred);
            if (qTrimmed) params.q = qTrimmed;
            return apiGet<ConversationResponse[]>("/conversations", { params });
        },
        getNextPageParam: (lastPage, allPages) => {
            // If last page has fewer items than limit, we've reached the end
            if (lastPage.length < limit) {
                return undefined;
            }
            return allPages.length * limit;
        },
        initialPageParam: 0,
    });
}

export function useLatestConversation(includeMessages = true) {
    return useQuery({
        queryKey: CONVERSATIONS_QUERY_KEYS.latest(includeMessages),
        queryFn: async (): Promise<ConversationResponse | null> => {
            try {
                return await apiGet<ConversationResponse>("/conversations/latest", {
                    params: { include_messages: includeMessages },
                });
            } catch {
                return null;
            }
        },
    });
}

export function useConversation(conversationId: string, includeMessages = true) {
    return useQuery({
        queryKey: CONVERSATIONS_QUERY_KEYS.detail(conversationId),
        queryFn: async (): Promise<ConversationResponse> => {
            return apiGet<ConversationResponse>(`/conversations/${conversationId}`, {
                params: { include_messages: includeMessages },
            });
        },
        enabled: !!conversationId,
    });
}

export function useDeleteConversation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (conversationId: string) => {
            await apiDelete(`/conversations/${conversationId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEYS.all });
        },
    });
}

export function useUpdateConversation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ conversationId, data }: { conversationId: string; data: ConversationUpdate }) => {
            return apiPatch<ConversationResponse>(`/conversations/${conversationId}`, data);
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEYS.all });
            queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEYS.detail(data.conversation_id) });
        },
    });
}

export function useToggleStarConversation() {
    const updateConversation = useUpdateConversation();

    return useMutation({
        mutationFn: async ({ conversationId, starred }: { conversationId: string; starred: boolean }) => {
            return updateConversation.mutateAsync({ conversationId, data: { starred } });
        },
    });
}

export function useBulkDeleteConversations() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (conversationIds: string[]) => {
            await apiPost("/conversations/bulk-delete", { conversation_ids: conversationIds });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEYS.all });
        },
    });
}
